// A clip's speed curve (spec docs/specs/editor/clip-speed-curves.md): its speed along its own file
// span, as points joined by straight lines in log speed, and what follows from it for every path
// that plays the clip: how long it lasts, where in its file it is at a time, and its steps at one
// speed each, for a path that plays one speed at a time (ffmpeg's `atempo`). Pure: no React, no API.

/** A speed's range, one speed's and a curve's (spec clip-speed, and clip-speed-curves decision 2):
 * the range in which every browser plays its sound. */
export const MIN_SPEED = 0.25;
export const MAX_SPEED = 4;

/** A point of a curve: `at`, its place in the clip's file span (0 to 1), and the speed there. */
export interface CurvePoint {
  at: number;
  speed: number;
}

/** A preset's name, as the contract's `speed_curve` names it (its other name is `custom`). */
export type CurvePreset = "montage" | "hero" | "bullet" | "jump_cut" | "flash_in" | "flash_out";

const point = (at: number, speed: number): CurvePoint => ({ at, speed });

/** Our presets, drawn to CapCut's names: CapCut documents no values (the layouts page). */
export const CURVE_PRESETS: Readonly<Record<CurvePreset, readonly CurvePoint[]>> = {
  montage: [point(0, 1), point(0.2, 2.5), point(0.4, 0.6), point(0.6, 2.5), point(0.8, 0.6), point(1, 1)],
  hero: [point(0, 2), point(0.38, 2), point(0.5, 0.4), point(0.62, 2), point(1, 2)],
  bullet: [point(0, 1), point(0.3, 1), point(0.42, 0.25), point(0.7, 0.25), point(0.82, 1), point(1, 1)],
  jump_cut: [point(0, 1), point(0.42, 1), point(0.46, 4), point(0.54, 4), point(0.58, 1), point(1, 1)],
  flash_in: [point(0, 4), point(0.3, 1), point(1, 1)],
  flash_out: [point(0, 1), point(0.7, 1), point(1, 4)],
};

/** The curve a Custom one starts from: five points at 1×. */
export const CUSTOM_START: readonly CurvePoint[] = [0, 0.25, 0.5, 0.75, 1].map((at) => point(at, 1));

/** The least distance between two points' places, a hundredth of the span. */
const NEAREST = 0.01;

/**
 * A curve held to its rules: at least two points, the first at 0 and the last at 1, each a
 * hundredth or more after the one before; speeds 0.25× to 4× in hundredths.
 */
export function heldCurve(points: readonly CurvePoint[]): CurvePoint[] {
  const speedOf = (speed: number) =>
    Math.round(Math.min(MAX_SPEED, Math.max(MIN_SPEED, Number.isFinite(speed) ? speed : 1)) * 100) / 100;
  const sorted = points
    .filter((each) => Number.isFinite(each.at))
    .map((each) => point(Math.min(1, Math.max(0, each.at)), speedOf(each.speed)))
    .sort((one, other) => one.at - other.at);
  const held: CurvePoint[] = [];
  for (const each of sorted) {
    const last = held.at(-1);
    if (last === undefined || each.at - last.at >= NEAREST) held.push(each);
  }
  const first = held[0] ?? point(0, 1);
  const end = held.at(-1) ?? first;
  if (held.length === 0) held.push(first);
  held[0] = point(0, first.speed);
  if (held.length === 1 || end.at < 1) {
    if (held.length > 1 && 1 - end.at < NEAREST) held[held.length - 1] = point(1, end.speed);
    else held.push(point(1, end.speed));
  }
  return held;
}

/** The segment of `points` that `at` lies in, with its share of the way along it. */
function segmentAt(points: readonly CurvePoint[], at: number): { from: CurvePoint; to: CurvePoint; share: number } {
  const x = Math.min(1, Math.max(0, at));
  for (let index = 1; index < points.length; index += 1) {
    const from = points[index - 1]!;
    const to = points[index]!;
    if (x <= to.at || index === points.length - 1) {
      const width = to.at - from.at;
      return { from, to, share: width <= 0 ? 0 : Math.min(1, Math.max(0, (x - from.at) / width)) };
    }
  }
  const only = points[0] ?? point(0, 1);
  return { from: only, to: only, share: 0 };
}

/** The speed at `at` (0 to 1): between two points, a straight line in log speed. */
export function speedAt(points: readonly CurvePoint[], at: number): number {
  const { from, to, share } = segmentAt(points, at);
  return from.speed * (to.speed / from.speed) ** share;
}

/**
 * The time a segment of the curve takes, from its start to `share` of the way along, for a file
 * span of 1: the integral of 1 / speed, exact for a speed straight in log.
 */
function timeAlong(from: CurvePoint, to: CurvePoint, share: number): number {
  const width = (to.at - from.at) * share;
  const ratio = to.speed / from.speed;
  if (width <= 0) return 0;
  if (Math.abs(ratio - 1) < 1e-9) return width / from.speed;
  const log = Math.log(ratio);
  return ((to.at - from.at) / from.speed) * ((1 - ratio ** -share) / log);
}

/** How long a clip lasts at the curve, over its file span at 1×: the mean of 1 / speed. */
export function lengthFactor(points: readonly CurvePoint[]): number {
  let total = 0;
  for (let index = 1; index < points.length; index += 1) total += timeAlong(points[index - 1]!, points[index]!, 1);
  return total;
}

/** A curve's time map: its file place at a time of the clip, and back, both as shares of 0 to 1. */
export interface TimeMap {
  /** The clip's length over its file span at 1× (`lengthFactor`). */
  factor: number;
  /** The share of the clip's time at which it reaches `file` of its file span. */
  timeAt: (file: number) => number;
  /** The share of its file span the clip reads at `time` of its own time. */
  fileAt: (time: number) => number;
}

export function timeMap(points: readonly CurvePoint[]): TimeMap {
  const curve = points.length >= 2 ? points : heldCurve(points);
  // The time each segment starts at, for a file span of 1.
  const starts = [0];
  for (let index = 1; index < curve.length; index += 1) {
    starts.push(starts[index - 1]! + timeAlong(curve[index - 1]!, curve[index]!, 1));
  }
  const factor = starts.at(-1) ?? 1;
  const timeAt = (file: number) => {
    const x = Math.min(1, Math.max(0, file));
    let index = 1;
    while (index < curve.length - 1 && x > curve[index]!.at) index += 1;
    const from = curve[index - 1]!;
    const to = curve[index]!;
    const share = to.at - from.at <= 0 ? 0 : (x - from.at) / (to.at - from.at);
    return factor <= 0 ? 0 : (starts[index - 1]! + timeAlong(from, to, share)) / factor;
  };
  const fileAt = (time: number) => {
    const t = Math.min(1, Math.max(0, time)) * factor;
    let index = 1;
    while (index < curve.length - 1 && t > starts[index]!) index += 1;
    const from = curve[index - 1]!;
    const to = curve[index]!;
    const left = t - starts[index - 1]!;
    const width = to.at - from.at;
    if (width <= 0) return from.at;
    const ratio = to.speed / from.speed;
    // Inverts `timeAlong`: the share whose time is `left`.
    const share =
      Math.abs(ratio - 1) < 1e-9
        ? (left * from.speed) / width
        : -Math.log(Math.max(1e-12, 1 - (left * from.speed * Math.log(ratio)) / width)) / Math.log(ratio);
    return Math.min(1, Math.max(0, from.at + Math.min(1, Math.max(0, share)) * width));
  };
  return { factor, timeAt, fileAt };
}

/** A step of a curve at one speed: a span of the clip's time, the file span it reads, at their ratio. */
export interface SpeedStep {
  fromMs: number;
  toMs: number;
  /** Its file span, as shares of the clip's. */
  fileFrom: number;
  fileTo: number;
  /** Its file span over its time: the speed it plays at. */
  speed: number;
}

/**
 * The curve as steps at one speed each, for a path that plays one speed at a time (the fallback's
 * `atempo`, whose mirror is `render/curves.py`'s `speed_steps`): steps of `stepMs` of the clip's
 * time, each at the speed that reads its file span in its time, so they add up to the curve's
 * length exactly. Neighbours at one speed are one step.
 */
export function speedSteps(points: readonly CurvePoint[], lengthMs: number, fileMs: number, stepMs = 250): SpeedStep[] {
  const map = timeMap(points);
  const count = Math.max(1, Math.ceil(lengthMs / Math.max(1, stepMs)));
  const steps: SpeedStep[] = [];
  for (let index = 0; index < count; index += 1) {
    const from = index / count;
    const to = (index + 1) / count;
    const fileFrom = map.fileAt(from);
    const fileTo = map.fileAt(to);
    const fromMs = from * lengthMs;
    const toMs = to * lengthMs;
    const speed = toMs > fromMs ? ((fileTo - fileFrom) * fileMs) / (toMs - fromMs) : 1;
    const last = steps.at(-1);
    if (last !== undefined && Math.abs(last.speed - speed) < 1e-6) {
      steps[steps.length - 1] = { ...last, toMs, fileTo };
    } else steps.push({ fromMs, toMs, fileFrom, fileTo, speed });
  }
  return steps;
}

/** A point of a clip's curve as the clip keeps it: its speed at `atMs` of its own file (the contract's). */
export interface SpeedPoint {
  atMs: number;
  speed: number;
}

/** A clip's curve: at most 16 points, each 10 ms or more of its file after the one before (the contract's). */
export const MAX_SPEED_POINTS = 16;
export const SPEED_POINT_GAP_MS = 10;

/**
 * A clip's points held to the contract: whole milliseconds of its file up to `maxFileMs`, in order,
 * each `SPEED_POINT_GAP_MS` or more after the one kept before it, at most `MAX_SPEED_POINTS`, speeds
 * 0.25× to 4× in hundredths. Fewer than two left are one speed over `fromMs` to `toMs`.
 */
export function heldSpeedPoints(
  points: readonly SpeedPoint[],
  maxFileMs: number,
  fromMs: number,
  toMs: number,
): SpeedPoint[] {
  const speedOf = (speed: number) =>
    Math.round(Math.min(MAX_SPEED, Math.max(MIN_SPEED, Number.isFinite(speed) ? speed : 1)) * 100) / 100;
  const sorted = points
    .filter((each) => Number.isFinite(each.atMs))
    .map((each) => ({ atMs: Math.round(Math.min(maxFileMs, Math.max(0, each.atMs))), speed: speedOf(each.speed) }))
    .sort((one, other) => one.atMs - other.atMs);
  const held: SpeedPoint[] = [];
  for (const each of sorted) {
    const last = held.at(-1);
    if (held.length < MAX_SPEED_POINTS && (last === undefined || each.atMs - last.atMs >= SPEED_POINT_GAP_MS)) held.push(each);
  }
  if (held.length >= 2) return held;
  const speed = held[0]?.speed ?? 1;
  return [
    { atMs: fromMs, speed },
    { atMs: Math.max(toMs, fromMs + SPEED_POINT_GAP_MS), speed },
  ];
}

/** A curve laid over a file span from `inMs`: its places (0 to 1) as times of the file. */
export function pointsOver(curve: readonly CurvePoint[], inMs: number, spanMs: number): SpeedPoint[] {
  return curve.map((each) => ({ atMs: inMs + each.at * spanMs, speed: each.speed }));
}

/** The speed at a time of a clip's file: straight in log speed between its points, flat past them. */
function speedAtFile(points: readonly SpeedPoint[], fileMs: number): number {
  const after = points.findIndex((each) => each.atMs > fileMs);
  const last = points.at(-1);
  if (last === undefined) return 1;
  if (after < 0) return last.speed;
  if (after === 0) return points[0]!.speed;
  const from = points[after - 1]!;
  const to = points[after]!;
  return from.speed * (to.speed / from.speed) ** ((fileMs - from.atMs) / (to.atMs - from.atMs));
}

/**
 * A clip's curve over its file span, as places of it (0 to 1): its points within the span, and one
 * at each end where the curve crosses it, so a split or a trim plays its own part of the curve.
 */
export function curveOver(points: readonly SpeedPoint[], inMs: number, spanMs: number): CurvePoint[] {
  const head = speedAtFile(points, inMs);
  if (spanMs <= 0) return [point(0, head), point(1, head)];
  const inner = points
    .filter((each) => each.atMs > inMs && each.atMs < inMs + spanMs)
    .map((each) => point((each.atMs - inMs) / spanMs, each.speed));
  return [point(0, head), ...inner, point(1, speedAtFile(points, inMs + spanMs))];
}

/**
 * What a clip's time map is read from: a video or a sound of the timeline, or the engine's picture
 * or sound of one. With a curve, `speed` is its mean: its file span over its length.
 */
export interface Sped {
  startMs: number;
  endMs: number;
  inMs: number;
  speed: number;
  speedPoints: readonly SpeedPoint[];
}

/** Whether a clip plays a curve: two points or more. */
export function curved(clip: Pick<Sped, "speedPoints">): boolean {
  return clip.speedPoints.length >= 2;
}

/**
 * The time a curve takes to read its file from its first point to `fileMs`, at 1× of its speeds
 * (`∫ du / speed(u)`): straight in log between points, so each segment's has a closed form, and
 * flat past the outermost points. Negative before the first.
 */
function readTime(points: readonly SpeedPoint[], fileMs: number): number {
  const first = points[0]!;
  if (fileMs <= first.atMs) return (fileMs - first.atMs) / first.speed;
  let total = 0;
  for (let index = 1; index < points.length; index += 1) {
    const from = points[index - 1]!;
    const to = points[index]!;
    const width = to.atMs - from.atMs;
    const into = Math.min(fileMs, to.atMs) - from.atMs;
    total += timeAlong(point(0, from.speed), point(1, to.speed), into / width) * width;
    if (fileMs <= to.atMs) return total;
  }
  const last = points.at(-1)!;
  return total + (fileMs - last.atMs) / last.speed;
}

/** Its inverse: the time of the file a curve reaches after `time` from its first point. */
function readTo(points: readonly SpeedPoint[], time: number): number {
  const first = points[0]!;
  if (time <= 0) return first.atMs + time * first.speed;
  let total = 0;
  for (let index = 1; index < points.length; index += 1) {
    const from = points[index - 1]!;
    const to = points[index]!;
    const width = to.atMs - from.atMs;
    const whole = timeAlong(point(0, from.speed), point(1, to.speed), 1) * width;
    if (time <= total + whole) {
      const left = (time - total) / width;
      const ratio = to.speed / from.speed;
      // Inverts `timeAlong`: the share of the segment whose time is `left`.
      const share =
        Math.abs(ratio - 1) < 1e-9
          ? left * from.speed
          : -Math.log(Math.max(1e-12, 1 - left * from.speed * Math.log(ratio))) / Math.log(ratio);
      return from.atMs + Math.min(1, Math.max(0, share)) * width;
    }
    total += whole;
  }
  const last = points.at(-1)!;
  return last.atMs + (time - total) * last.speed;
}

/**
 * A curved clip's map: when it reads its in-point along its curve, and the scale that makes its
 * span last its length in whole milliseconds (its speeds scaled by that rounding, a hair).
 */
function clipMap(clip: Sped) {
  const lengthMs = clip.endMs - clip.startMs;
  const from = readTime(clip.speedPoints, clip.inMs);
  const span = readTime(clip.speedPoints, clip.inMs + lengthMs * clip.speed) - from;
  return { from, pace: lengthMs > 0 && span > 0 ? lengthMs / span : 1 };
}

/**
 * Where a clip reads its file at `atMs` of the timeline, unrounded: its in-point moved at its speed,
 * or along its curve's time map (spec clip-speed-curves). Before its start and past its end it
 * reads on along the curve's points it keeps there (a trimmed part's), flat past the outermost, as
 * far as a trim of it reaches, so a trim taken back gives back what it took.
 */
export function fileMsAt(clip: Sped, atMs: number): number {
  if (!curved(clip)) return clip.inMs + (atMs - clip.startMs) * clip.speed;
  const { from, pace } = clipMap(clip);
  return readTo(clip.speedPoints, from + (atMs - clip.startMs) / pace);
}

/** Its inverse: when on the timeline a clip reads `fileMs` of its file, unrounded. */
export function clipMsAt(clip: Sped, fileMs: number): number {
  if (!curved(clip)) return clip.startMs + (fileMs - clip.inMs) / clip.speed;
  const { from, pace } = clipMap(clip);
  return clip.startMs + (readTime(clip.speedPoints, fileMs) - from) * pace;
}

/**
 * The speed a clip plays at `atMs` of the timeline: its one speed, or its curve's there, held to the
 * range a browser plays (the rounding's scale can put a 4× part a hair past 4×, which Gecko mutes).
 */
export function speedAtMs(clip: Sped, atMs: number): number {
  if (!curved(clip)) return clip.speed;
  const { pace } = clipMap(clip);
  const held = Math.min(clip.endMs, Math.max(clip.startMs, atMs));
  return Math.min(MAX_SPEED, Math.max(MIN_SPEED, speedAtFile(clip.speedPoints, fileMsAt(clip, held)) / pace));
}
